/**
 * Knowledge · Categories — the top of the hierarchy: Category → Subject →
 * Guide. Curator called these Categories; the domain model calls them
 * templates. The UI says Category, because that's the word a curator uses.
 *
 * Creating one is a guided sequence (CategoryWizard) rather than a form: a
 * category can't generate until it has a briefing and per-stage instructions,
 * so those are steps, not settings to discover later.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Button, Skeleton, Table } from '@servicecompressionllc/sc-design-system';
import { useMe } from '../../hooks/useMe.js';
import { canWriteKnowledge } from '../../lib/knowledgeAccess.js';
import { knowledgeApi } from './api.js';

export const CategoriesPage = () => {
  const navigate = useNavigate();
  const me = useMe();
  const canWrite = me.data ? canWriteKnowledge(me.data.user) : false;
  const templates = useQuery({
    queryKey: ['knowledge', 'templates'],
    queryFn: knowledgeApi.templates,
  });
  if (templates.isLoading) return <Skeleton className="h-40" />;
  if (templates.error) {
    return <div className="text-sm text-danger">Couldn’t load categories.</div>;
  }
  const rows = templates.data ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <section className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-ink">Categories</h2>
          <p className="text-sm text-ink-muted">The kinds of thing your knowledge base covers.</p>
        </div>
        {canWrite && (
          <Button onClick={() => void navigate('/knowledge/categories/new')}>New category</Button>
        )}
      </section>

      <div className="overflow-x-auto rounded-lg border border-outline bg-surface-elevated">
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Category</Table.HeaderCell>
              <Table.HeaderCell>Calls a subject</Table.HeaderCell>
              <Table.HeaderCell>Calls a guide</Table.HeaderCell>
              <Table.HeaderCell>Subjects</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rows.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={4} className="text-ink-subtle">
                  No categories yet
                  {canWrite ? ' — create one to start adding subjects.' : '.'}
                </Table.Cell>
              </Table.Row>
            )}
            {rows.map((t) => (
              <Table.Row
                key={t.id}
                className="cursor-pointer hover:bg-surface-hover"
                onClick={() => void navigate(`/knowledge/categories/${t.id}`)}
              >
                <Table.Cell>
                  <span className="font-medium text-ink">{t.name}</span>
                </Table.Cell>
                <Table.Cell className="text-ink-muted">{t.subjectNoun}</Table.Cell>
                <Table.Cell className="text-ink-muted">{t.itemNoun}</Table.Cell>
                <Table.Cell className="text-ink-muted">{t.subjectCount}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </div>
    </div>
  );
};
